import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Search, X, Loader2, User, Phone, CalendarDays, TrendingUp, Clock, Check, Sparkles, StickyNote } from "lucide-react";

interface GuestProfile { name: string; phone?: string; visits: number; spend: number; last_visit?: string; services: string[]; notes_record?: { preferences?: string; health_notes?: string; notes?: string; } | null; }
interface Appointment { id: number; service_name?: string; staff_name?: string; date: string; time_slot: string; duration: number; price: number; status: string; notes?: string; }
function authH() { return { Authorization: `Bearer ${localStorage.getItem("ai2_token")}`, "x-hotel": localStorage.getItem("ai2_hotel") || "" }; }
function fmt12(t?: string) { if (!t) return ""; const [h, m] = t.split(":").map(Number); const ap = h >= 12 ? "PM" : "AM"; return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${ap}`; }

const STATUS_COLOR: Record<string, string> = { confirmed: "#818cf8", arrived: "#fbbf24", in_progress: "#34d399", completed: "#10b981", cancelled: "#f87171", no_show: "#94a3b8" };

export default function SpaGuests({ API }: { API: string }) {
  const [guests, setGuests]     = useState<GuestProfile[]>([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState("");
  const [selected, setSelected] = useState<GuestProfile | null>(null);
  const [history, setHistory]   = useState<Appointment[]>([]);
  const [histLoading, setHistLoading] = useState(false);
  const [editNotes, setEditNotes]     = useState(false);
  const [noteForm, setNoteForm] = useState({ preferences: "", health_notes: "", notes: "" });
  const [saving, setSaving]     = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/guests`, { headers: authH() }); setGuests(data); }
    catch { /**/ } finally { setLoading(false); }
  }, [API]);

  useEffect(() => { fetch(); }, [fetch]);

  const openGuest = async (g: GuestProfile) => {
    setSelected(g);
    setNoteForm({ preferences: g.notes_record?.preferences || "", health_notes: g.notes_record?.health_notes || "", notes: g.notes_record?.notes || "" });
    setEditNotes(false);
    setHistLoading(true);
    try { const { data } = await axios.get(`${API}/guests/history?name=${encodeURIComponent(g.name)}`, { headers: authH() }); setHistory(data); }
    catch { /**/ } finally { setHistLoading(false); }
  };

  const saveNotes = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await axios.post(`${API}/guests/notes`, { guest_name: selected.name, phone: selected.phone, ...noteForm }, { headers: authH() });
      setSelected(s => s ? { ...s, notes_record: { ...noteForm } } : null);
      setEditNotes(false); fetch();
    } catch { /**/ } finally { setSaving(false); }
  };

  const filtered = guests.filter(g => !search || g.name?.toLowerCase().includes(search.toLowerCase()) || g.phone?.includes(search));

  return (
    <div style={{ display: "flex", gap: 20, minHeight: 400 }}>
      {/* Guest list */}
      <div style={{ flex: "0 0 340px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(201,168,76,0.12)", borderRadius: 9, padding: "8px 12px", marginBottom: 14 }}>
          <Search size={13} color="rgba(242,235,217,0.45)" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search guests…" style={{ background: "none", border: "none", outline: "none", color: "#F2EBD9", fontSize: 13, width: "100%", fontFamily: "inherit" }} />
        </div>
        {loading ? <div className="spa-loading"><Loader2 size={18} className="spa-spinner" /> Loading…</div> : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 560, overflowY: "auto" }}>
            {filtered.map(g => (
              <div key={g.name} onClick={() => openGuest(g)} style={{ background: selected?.name === g.name ? "rgba(201,168,76,0.12)" : "#0E1E38", border: `1px solid ${selected?.name === g.name ? "rgba(201,168,76,0.4)" : "rgba(201,168,76,0.1)"}`, borderRadius: 10, padding: "12px 14px", cursor: "pointer" }}>
                <div style={{ fontWeight: 700, color: "#F2EBD9", fontSize: 13 }}>{g.name}</div>
                <div style={{ fontSize: 11, color: "rgba(242,235,217,0.4)", marginTop: 3, display: "flex", gap: 10 }}>
                  <span><CalendarDays size={10} style={{ display: "inline", marginRight: 3 }} />{g.visits} visit{g.visits !== 1 ? "s" : ""}</span>
                  <span><TrendingUp size={10} style={{ display: "inline", marginRight: 3 }} />${g.spend.toFixed(0)}</span>
                  {g.last_visit && <span>Last: {g.last_visit}</span>}
                </div>
              </div>
            ))}
            {filtered.length === 0 && <div style={{ color: "rgba(242,235,217,0.3)", fontSize: 13, textAlign: "center", padding: 20 }}>No guests found</div>}
          </div>
        )}
      </div>

      {/* Detail panel */}
      {selected ? (
        <div style={{ flex: 1, background: "#0b1120", border: "1px solid rgba(201,168,76,0.12)", borderRadius: 14, padding: 20, overflowY: "auto", maxHeight: 600 }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 18 }}>
            <div>
              <div style={{ fontSize: 17, fontWeight: 800, color: "#F2EBD9" }}>{selected.name}</div>
              {selected.phone && <div style={{ fontSize: 12, color: "rgba(242,235,217,0.45)", marginTop: 3 }}><Phone size={11} style={{ display: "inline", marginRight: 4 }} />{selected.phone}</div>}
            </div>
            <button className="spa-modal-close" onClick={() => setSelected(null)}><X size={16} /></button>
          </div>

          {/* Stats row */}
          <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
            {[
              { icon: <CalendarDays size={14} />, label: "Visits", value: selected.visits },
              { icon: <TrendingUp size={14} />, label: "Spend", value: `$${selected.spend.toFixed(0)}` },
              { icon: <Clock size={14} />, label: "Last Visit", value: selected.last_visit || "—" },
            ].map(s => (
              <div key={s.label} style={{ flex: 1, background: "rgba(201,168,76,0.06)", border: "1px solid rgba(201,168,76,0.1)", borderRadius: 10, padding: "10px 14px" }}>
                <div style={{ color: "#c9a84c", marginBottom: 4 }}>{s.icon}</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: "#F2EBD9" }}>{s.value}</div>
                <div style={{ fontSize: 11, color: "rgba(242,235,217,0.4)" }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Favourite services */}
          {selected.services.length > 0 && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "rgba(242,235,217,0.45)", marginBottom: 8 }}>Services</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {selected.services.map(s => <span key={s} style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, background: "rgba(201,168,76,0.1)", border: "1px solid rgba(201,168,76,0.2)", color: "#c9a84c" }}>{s}</span>)}
              </div>
            </div>
          )}

          {/* Notes / Preferences */}
          <div style={{ marginBottom: 18, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 10, padding: 14 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "rgba(242,235,217,0.45)" }}><StickyNote size={11} style={{ display: "inline", marginRight: 4 }} />Profile Notes</div>
              {!editNotes && <button className="spa-btn-ghost" style={{ fontSize: 11, padding: "3px 10px" }} onClick={() => setEditNotes(true)}>Edit</button>}
            </div>
            {editNotes ? (
              <>
                <div className="spa-form-group" style={{ marginBottom: 8 }}><label style={{ fontSize: 11 }}>Preferences</label><textarea rows={2} value={noteForm.preferences} onChange={e => setNoteForm(f => ({ ...f, preferences: e.target.value }))} placeholder="e.g. Prefers female therapist, firm pressure…" /></div>
                <div className="spa-form-group" style={{ marginBottom: 8 }}><label style={{ fontSize: 11 }}>Health Notes</label><textarea rows={2} value={noteForm.health_notes} onChange={e => setNoteForm(f => ({ ...f, health_notes: e.target.value }))} placeholder="e.g. Lower back pain, nut allergy…" /></div>
                <div className="spa-form-group" style={{ marginBottom: 10 }}><label style={{ fontSize: 11 }}>General Notes</label><textarea rows={2} value={noteForm.notes} onChange={e => setNoteForm(f => ({ ...f, notes: e.target.value }))} placeholder="Other notes…" /></div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button className="spa-btn-ghost" style={{ fontSize: 12 }} onClick={() => setEditNotes(false)}>Cancel</button>
                  <button className="spa-btn-primary" style={{ fontSize: 12 }} onClick={saveNotes} disabled={saving}>{saving ? <Loader2 size={12} className="spa-spinner" /> : <Check size={12} />} Save</button>
                </div>
              </>
            ) : (
              <>
                {noteForm.preferences && <div style={{ fontSize: 12, color: "rgba(242,235,217,0.6)", marginBottom: 6 }}><span style={{ color: "rgba(242,235,217,0.35)", fontSize: 11 }}>Preferences: </span>{noteForm.preferences}</div>}
                {noteForm.health_notes && <div style={{ fontSize: 12, color: "rgba(242,235,217,0.6)", marginBottom: 6 }}><span style={{ color: "rgba(242,235,217,0.35)", fontSize: 11 }}>Health: </span>{noteForm.health_notes}</div>}
                {noteForm.notes && <div style={{ fontSize: 12, color: "rgba(242,235,217,0.6)" }}><span style={{ color: "rgba(242,235,217,0.35)", fontSize: 11 }}>Notes: </span>{noteForm.notes}</div>}
                {!noteForm.preferences && !noteForm.health_notes && !noteForm.notes && <div style={{ fontSize: 12, color: "rgba(242,235,217,0.25)" }}>No notes yet — click Edit to add.</div>}
              </>
            )}
          </div>

          {/* History */}
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "rgba(242,235,217,0.45)", marginBottom: 10 }}>Appointment History</div>
          {histLoading ? <div className="spa-loading"><Loader2 size={14} className="spa-spinner" /> Loading…</div> : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {history.map(a => (
                <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", background: "rgba(255,255,255,0.02)", borderRadius: 8, border: "1px solid rgba(255,255,255,0.04)" }}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: STATUS_COLOR[a.status] || "#94a3b8", flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "rgba(242,235,217,0.8)" }}>{a.service_name || "—"}</div>
                    <div style={{ fontSize: 11, color: "rgba(242,235,217,0.4)" }}>{a.date} · {fmt12(a.time_slot)}{a.staff_name ? ` · ${a.staff_name}` : ""}</div>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#c9a84c" }}>${a.price}</div>
                </div>
              ))}
              {history.length === 0 && <div style={{ fontSize: 12, color: "rgba(242,235,217,0.3)", textAlign: "center", padding: 12 }}>No appointments found</div>}
            </div>
          )}
        </div>
      ) : (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(242,235,217,0.2)", fontSize: 14 }}>
          <div style={{ textAlign: "center" }}><User size={36} style={{ marginBottom: 10, opacity: 0.3 }} /><br />Select a guest to view profile</div>
        </div>
      )}
    </div>
  );
}
